import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Bell, 
  CheckCheck,
  Trash2,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Send,
  Users,
  User,
  Search,
  X,
  Plus,
  Megaphone
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const notificationIcons = {
  info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-100' },
  warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-100' },
  success: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' }
};

export default function NotificationsPage() {
  const { user, token, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendMode, setSendMode] = useState('all'); // 'all' or 'selected'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'info'
  });
  const [sending, setSending] = useState(false);
  const { refreshCounts } = useOutletContext() || {};

  useEffect(() => {
    fetchNotifications();
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Exclure l'utilisateur actuel de la liste
      setAllUsers(response.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleMarkRead = async (notification) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notification.id}/read`);
      fetchNotifications();
      refreshCounts?.();
    } catch (error) {
      console.error('Failed to mark as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`);
      toast.success('Toutes les notifications ont été marquées comme lues');
      fetchNotifications();
      refreshCounts?.();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (notification) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${notification.id}`);
      toast.success('Notification supprimée');
      fetchNotifications();
      refreshCounts?.();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSendNotification = async () => {
    if (!notificationForm.title.trim() || !notificationForm.message.trim()) {
      toast.error('Veuillez remplir le titre et le message');
      return;
    }

    if (sendMode === 'selected' && selectedUsers.length === 0) {
      toast.error('Veuillez sélectionner au moins un utilisateur');
      return;
    }

    setSending(true);
    try {
      const payload = {
        title: notificationForm.title,
        message: notificationForm.message,
        type: notificationForm.type,
        send_to_all: sendMode === 'all',
        user_ids: sendMode === 'selected' ? selectedUsers.map(u => u.id) : []
      };

      await axios.post(`${API_URL}/api/notifications/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const recipientCount = sendMode === 'all' ? allUsers.length : selectedUsers.length;
      toast.success(`Notification envoyée à ${recipientCount} utilisateur${recipientCount > 1 ? 's' : ''}`);
      
      // Reset form
      setSendDialogOpen(false);
      setNotificationForm({ title: '', message: '', type: 'info' });
      setSelectedUsers([]);
      setSendMode('all');
      setSearchUser('');
    } catch (error) {
      console.error('Failed to send notification:', error);
      toast.error('Erreur lors de l\'envoi de la notification');
    } finally {
      setSending(false);
    }
  };

  const toggleUserSelection = (userToToggle) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === userToToggle.id);
      if (isSelected) {
        return prev.filter(u => u.id !== userToToggle.id);
      } else {
        return [...prev, userToToggle];
      }
    });
  };

  const removeSelectedUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const filteredUsers = allUsers.filter(u =>
    u.first_name?.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchUser.toLowerCase())
  );

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-7 h-7" />
            Notifications
          </h1>
          <p className="text-slate-500 mt-1">
            {unreadCount > 0 
              ? `Vous avez ${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
              : 'Toutes vos notifications sont à jour'
            }
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button 
              onClick={() => setSendDialogOpen(true)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="send-notification-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Envoyer une notification
            </Button>
          )}
          {unreadCount > 0 && (
            <Button 
              variant="outline"
              onClick={handleMarkAllRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Tout marquer comme lu
            </Button>
          )}
        </div>
      </div>

      {/* Notifications list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Toutes les notifications
            {unreadCount > 0 && (
              <Badge className="bg-blue-100 text-blue-700">{unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Bell className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">Aucune notification</p>
              <p className="text-sm">Vous n'avez pas encore de notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => {
                const iconConfig = notificationIcons[notification.type] || notificationIcons.info;
                const Icon = iconConfig.icon;
                
                return (
                  <div 
                    key={notification.id}
                    className={`flex items-start gap-4 p-4 rounded-lg border transition-all ${
                      !notification.is_read
                        ? 'bg-blue-50/50 border-blue-100'
                        : 'bg-white border-slate-100 hover:bg-slate-50'
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className={`p-2 rounded-lg ${iconConfig.bg}`}>
                      <Icon className={`w-5 h-5 ${iconConfig.color}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${!notification.is_read ? 'text-slate-900' : 'text-slate-700'}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Nouveau</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notification.created_at).toLocaleString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {!notification.is_read && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleMarkRead(notification)}
                          title="Marquer comme lu"
                          data-testid={`mark-read-${notification.id}`}
                        >
                          <CheckCheck className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(notification)}
                        className="text-slate-400 hover:text-destructive"
                        title="Supprimer"
                        data-testid={`delete-notification-${notification.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog d'envoi de notification (Admin) */}
      <Dialog open={sendDialogOpen} onOpenChange={(open) => {
        setSendDialogOpen(open);
        if (!open) {
          setNotificationForm({ title: '', message: '', type: 'info' });
          setSelectedUsers([]);
          setSendMode('all');
          setSearchUser('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              Envoyer une notification
            </DialogTitle>
            <DialogDescription>
              Envoyez une notification à tous les utilisateurs ou à des personnes spécifiques
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Titre */}
            <div className="space-y-2">
              <Label htmlFor="notif-title">Titre *</Label>
              <Input
                id="notif-title"
                value={notificationForm.title}
                onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                placeholder="Ex: Mise à jour importante"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="notif-message">Message *</Label>
              <Textarea
                id="notif-message"
                value={notificationForm.message}
                onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                placeholder="Le contenu de votre notification..."
                rows={3}
              />
            </div>

            {/* Type de notification */}
            <div className="space-y-2">
              <Label>Type de notification</Label>
              <Select 
                value={notificationForm.type} 
                onValueChange={(value) => setNotificationForm({ ...notificationForm, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-blue-600" />
                      Information
                    </div>
                  </SelectItem>
                  <SelectItem value="success">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      Succès
                    </div>
                  </SelectItem>
                  <SelectItem value="warning">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Avertissement
                    </div>
                  </SelectItem>
                  <SelectItem value="error">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      Erreur / Urgent
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Destinataires */}
            <div className="space-y-2">
              <Label>Destinataires</Label>
              <Tabs value={sendMode} onValueChange={setSendMode}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="all" className="text-sm">
                    <Users className="w-4 h-4 mr-2" />
                    Tous ({allUsers.length})
                  </TabsTrigger>
                  <TabsTrigger value="selected" className="text-sm">
                    <User className="w-4 h-4 mr-2" />
                    Sélection
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="mt-3">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <Users className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">
                      La notification sera envoyée à <strong>{allUsers.length} utilisateur{allUsers.length > 1 ? 's' : ''}</strong>
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="selected" className="mt-3 space-y-3">
                  {/* Utilisateurs sélectionnés */}
                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                      {selectedUsers.map((u) => (
                        <Badge 
                          key={u.id} 
                          variant="secondary" 
                          className="flex items-center gap-1 pr-1"
                        >
                          <span>{u.first_name} {u.last_name}</span>
                          <button
                            type="button"
                            onClick={() => removeSelectedUser(u.id)}
                            className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Recherche */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                      placeholder="Rechercher un utilisateur..."
                      className="pl-10"
                    />
                  </div>

                  {/* Liste des utilisateurs */}
                  <ScrollArea className="h-48 border rounded-lg">
                    {filteredUsers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Aucun utilisateur trouvé
                      </p>
                    ) : (
                      <div className="p-2 space-y-1">
                        {filteredUsers.map((u) => {
                          const isSelected = selectedUsers.some(s => s.id === u.id);
                          return (
                            <div
                              key={u.id}
                              onClick={() => toggleUserSelection(u)}
                              className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                                isSelected ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'
                              }`}
                            >
                              <div 
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  isSelected ? 'bg-white border-white' : 'border-slate-300'
                                }`}
                              >
                                {isSelected && (
                                  <svg className="w-3 h-3 text-slate-900" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                )}
                              </div>
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className={`text-xs ${isSelected ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                  {getInitials(u.first_name, u.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {u.first_name} {u.last_name}
                                </p>
                                <p className={`text-xs truncate ${isSelected ? 'text-slate-300' : 'text-muted-foreground'}`}>
                                  {u.email}
                                </p>
                              </div>
                              {u.role === 'admin' && (
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${isSelected ? 'text-amber-300 border-amber-300' : 'text-amber-600 border-amber-300'}`}
                                >
                                  Admin
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendNotification}
              disabled={sending || !notificationForm.title || !notificationForm.message || (sendMode === 'selected' && selectedUsers.length === 0)}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer {sendMode === 'all' ? `à tous (${allUsers.length})` : `(${selectedUsers.length})`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
